/**
 * Sentry initialization for Hotwire/Turbo pages (admin, support, docs).
 * Uses @sentry/browser (no React-specific integrations).
 *
 * On Inertia pages (which have a #app element), this is skipped because
 * sentry.ts initializes with @sentry/react and React-specific integrations.
 *
 * CONEK-4225: Sentry must never break the host app. All init is wrapped in
 * try/catch and browserTracingIntegration is removed (it monkey-patches
 * global fetch(), which propagated relay failures to user-facing requests
 * like POST /auth/check_email and locked users out of login).
 */
import * as Sentry from "@sentry/browser"

// Runtime config from Rails-rendered <meta> tags. See sentry.ts for context.
// `typeof document` guard keeps the module SSR-safe.
const metaContent = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined
  return document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
    ?.content
}

const dsn = metaContent("sentry-dsn")
const environment = metaContent("sentry-environment") ?? "development"
const isProduction = environment === "production"
const isInertiaPage = document.getElementById("app") !== null

// Skip auth routes — these are critical for app access and must never be
// coupled to telemetry health. Acceptable trade-off: no Sentry on login/logout.
const isAuthRoute =
  typeof window !== "undefined" &&
  /^\/(login|logout|auth)\b/.test(window.location.pathname)

if (dsn && !isInertiaPage && !isAuthRoute) {
  try {
    Sentry.init({
      dsn,
      environment,

      // browserTracingIntegration is intentionally omitted. It wraps global
      // fetch() to create performance spans. When the Sentry relay/ingest
      // endpoint is unreachable, the wrapper can propagate errors to unrelated
      // user requests. Error capture + session replay work without it.

      // Session Replay: 10% in production, none elsewhere; always 100% on
      // error. See sentry.ts for why dev and staging record no healthy
      // sessions (CONEK-7732).
      replaysSessionSampleRate: isProduction ? 0.1 : 0,
      replaysOnErrorSampleRate: 1.0,

      integrations: [Sentry.replayIntegration()],

      ignoreErrors: [
        "ResizeObserver loop",
        "Failed to fetch",
        "NetworkError",
        "Load failed",
      ],
    })
  } catch {
    // Sentry init failed — silently continue. The app must never crash
    // because telemetry is broken.
  }
}

export { Sentry }
